import { useCallback } from 'react';
import { formatVersionTime } from './utils';
import { VersionCardProps } from './types';
import Avatar from '@/components/atoms/Avatar/Avatar';
import TooltipV2 from '@/components/TooltipV2';

export default function VersionCard({
  version,
  isCurrentVersion,
  isViewingVersion,
  isRestoring,
  isDimmed,
  onView,
  onRestore
}: VersionCardProps) {
  const { displayIndex, restoredFromDisplayIndex, created_by: author } = version;
  // Falls back to the email, then to a neutral label: a version the backend created carries no
  // user, and an avatar still has to render something readable.
  const authorName = (author ? `${author.first_name} ${author.last_name}`.trim() || author.email : '') || 'Automatic';
  const isOriginalVersion = displayIndex === 0;
  const versionLabel = `Version ${displayIndex}`;

  const handleView = useCallback(() => {
    onView(version);
  }, [onView, version]);

  const handleRestore = useCallback(() => {
    onRestore(version);
  }, [onRestore, version]);

  return (
    <div
      className={`group flex cursor-pointer items-start gap-2 rounded-lg px-3 py-2 ${isViewingVersion ? 'bg-slate-100' : 'hover:bg-slate-50'} ${isDimmed ? 'pointer-events-none opacity-45' : ''}`}
    >
      {/* Initials only — the full name is the hover tooltip, since the card has no room for it. */}
      <div className="shrink-0">
        <TooltipV2
          tooltipId={`version-author-${version.id}`}
          tooltipContent={authorName}
          place="top"
          className="z-50"
          tooltipElement={<Avatar name={authorName} size="md" imageClassName="rounded-full" />}
        />
      </div>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {/* Title row */}
        <div className="flex items-center justify-between gap-2">
          {/* Version name + badges */}
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="whitespace-nowrap text-sm font-semibold tracking-[-0.14px] text-black">
              {versionLabel}
            </span>

            {isCurrentVersion && !isViewingVersion && (
              <span className="whitespace-nowrap rounded-full bg-green-100 px-1.5 py-px text-[10px] font-medium leading-[13px] text-green-700">
                Current
              </span>
            )}

            {isOriginalVersion && (
              <span className="whitespace-nowrap rounded-full bg-slate-100 px-1.5 py-px text-[10px] font-medium leading-[13px] text-slate-600">
                Original
              </span>
            )}

            {isViewingVersion && (
              <span className="whitespace-nowrap rounded-full bg-yellow-100 px-1.5 py-px text-[10px] font-medium leading-[13px] text-yellow-700">
                Viewing
              </span>
            )}
          </div>

          {/* Action button */}
          <div className="shrink-0">
            {isRestoring ? (
              <span
                className="flex h-6 min-w-[64px] items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-1 text-xs font-medium text-black"
                role="status"
              >
                <span className="size-[11px] shrink-0 animate-spin rounded-full border-[1.5px] border-black/[0.08] border-t-slate-900 [animation-duration:0.7s]" />
                Restoring…
              </span>
            ) : isViewingVersion ? (
              // Restore — always visible while in viewing state
              <button
                onClick={handleRestore}
                className="flex h-6 min-w-[36px] items-center justify-center rounded-md border border-slate-200 bg-white px-1 text-xs font-medium text-black"
                title={`Restore to Version ${displayIndex}`}
              >
                Restore
              </button>
            ) : (
              // View — appears on card hover, hidden for the current version
              !isCurrentVersion && (
                <button
                  onClick={handleView}
                  className="flex h-6 min-w-[36px] items-center justify-center rounded-md border border-slate-200 bg-white px-1 text-xs font-medium text-black opacity-0 transition-opacity group-hover:opacity-100"
                  title={`Preview Version ${displayIndex}`}
                >
                  View
                </button>
              )
            )}
          </div>
        </div>

        {/* Description */}
        <p className="text-sm leading-5 text-gray-500">
          {typeof restoredFromDisplayIndex === 'number'
            ? `This is a restore of Version ${restoredFromDisplayIndex}`
            : version.description}
        </p>

        {/* Timestamp */}
        <span className="text-[10px] font-medium leading-[13px] text-[#ff8032]">
          {formatVersionTime(version.created_at)}
        </span>
      </div>
    </div>
  );
}
